import { Controller } from '@hotwired/stimulus'
import { forceCollide, forceSimulation, forceX, forceY } from 'd3-force'

export default class extends Controller {
  static targets = ['circle']

  static values = {
    overlap: Number,
    padding: Number,
    verticalScale: Number
  }

  connect () {
    this.layoutRaf = null
    this.resizeHandler = () => this.scheduleLayout()

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout())
      this.resizeObserver.observe(this.element)
    } else {
      window.addEventListener('resize', this.resizeHandler, { passive: true })
    }

    this.scheduleLayout()
  }

  disconnect () {
    cancelAnimationFrame(this.layoutRaf)
    this.resizeObserver?.disconnect()
    window.removeEventListener('resize', this.resizeHandler)
  }

  layout () {
    const height = this.element.clientHeight
    const width = this.element.clientWidth

    if (!height || !width || !this.hasCircleTarget) return

    const padding = this.paddingValue || 0
    const availableHeight = Math.max(height - (padding * 2), 1)
    const availableWidth = Math.max(width - (padding * 2), 1)
    const circles = this.packCircles(availableWidth, availableHeight)
    const bounds = this.visualBounds(circles)
    const offsetX = padding + ((availableWidth - (bounds.maxX - bounds.minX)) / 2) - bounds.minX
    const offsetY = padding + ((availableHeight - (bounds.maxY - bounds.minY)) / 2) - bounds.minY

    circles.forEach((circle) => {
      const diameter = circle.innerRadius * 2
      const left = circle.x + offsetX - circle.innerRadius
      const top = circle.y + offsetY - circle.innerRadius

      circle.element.style.height = `${diameter}px`
      circle.element.style.left = `${left}px`
      circle.element.style.top = `${top}px`
      circle.element.style.transform = 'translate(0, 0)'
      circle.element.style.width = `${diameter}px`
      circle.element.style.zIndex = `${circle.zIndex}`
    })
  }

  packCircles (availableWidth, availableHeight) {
    const baseCircles = this.circleTargets.map((element, stackIndex) => ({
      element,
      innerRadius: Number(element.dataset.size) / 2,
      kind: element.dataset.kind,
      ringWidth: Number(element.dataset.ringWidth || 0),
      stackIndex
    }))
    let scale = 1
    let packedCircles = []

    for (let attempt = 0; attempt < 6; attempt++) {
      packedCircles = this.scaledCircles(baseCircles, scale, availableWidth, availableHeight)

      const bounds = this.visualBounds(packedCircles)
      const heightScale = availableHeight / (bounds.maxY - bounds.minY)
      const widthScale = availableWidth / (bounds.maxX - bounds.minX)
      const nextScale = scale * Math.min(heightScale, widthScale, 1)

      if (nextScale >= scale * 0.999) break

      scale = nextScale
    }

    return packedCircles
  }

  simulationCircles (baseCircles, scale, availableWidth, availableHeight) {
    const overlap = this.overlapValue || 0
    const verticalScale = this.verticalScaleValue || 1
    const orderedCircles = this.alternatingKinds(baseCircles)
    const cloudTargets = this.cloudTargets(baseCircles.length, availableWidth, availableHeight,
      verticalScale)
    const circles = orderedCircles.map((circle, index) => {
      const innerRadius = circle.innerRadius * scale
      const ringWidth = circle.ringWidth
      const drawRadius = innerRadius + ringWidth
      const target = cloudTargets[index]

      return {
        drawRadius,
        element: circle.element,
        innerRadius,
        kind: circle.kind,
        ringWidth,
        x: target.x,
        xTarget: target.x,
        y: target.y,
        yTarget: target.y
      }
    })

    this.alternatingKinds(circles).forEach((circle, index) => {
      circle.zIndex = index + 1
    })

    const yStrength = 0.07 + ((1 - verticalScale) * 0.08)
    const simulation = forceSimulation(circles)
      .alphaDecay(0.06)
      .force('collide', forceCollide((circle) => Math.max(circle.drawRadius - (overlap / 2), 1))
        .iterations(6))
      .force('x', forceX((circle) => circle.xTarget).strength(0.05))
      .force('y', forceY((circle) => circle.yTarget).strength(yStrength))
      .stop()

    for (let tick = 0; tick < 250; tick++) {
      simulation.tick()
    }

    return circles
  }

  scaledCircles (baseCircles, scale, availableWidth, availableHeight) {
    return this.simulationCircles(baseCircles, scale, availableWidth, availableHeight)
  }

  alternatingKinds (circles) {
    const practices = circles.filter((circle) => circle.kind === 'practice')
    const others = circles.filter((circle) => circle.kind !== 'practice')
    const orderedCircles = []

    while (practices.length || others.length) {
      if (practices.length) orderedCircles.push(practices.shift())
      if (others.length) orderedCircles.push(others.shift())
    }

    orderedCircles.push(...practices, ...others)

    return orderedCircles
  }

  cloudTargets (count, availableWidth, availableHeight, verticalScale) {
    const centerX = availableWidth / 2
    const centerY = availableHeight / 2
    const radiusStep = Math.min(availableWidth, availableHeight) * 0.13

    return Array.from({ length: count }, (_, index) => {
      const angle = index * 2.399963229728653
      const radius = Math.sqrt(index + 0.5) * radiusStep

      return {
        x: centerX + (Math.cos(angle) * radius * 1.6),
        y: centerY + (Math.sin(angle) * radius * verticalScale * 0.9)
      }
    })
  }

  scheduleLayout () {
    if (this.layoutRaf) return

    this.layoutRaf = requestAnimationFrame(() => {
      this.layoutRaf = null
      this.layout()
    })
  }

  visualBounds (circles) {
    return circles.reduce((bounds, circle) => ({
      maxX: Math.max(bounds.maxX, circle.x + circle.drawRadius),
      maxY: Math.max(bounds.maxY, circle.y + circle.drawRadius),
      minX: Math.min(bounds.minX, circle.x - circle.drawRadius),
      minY: Math.min(bounds.minY, circle.y - circle.drawRadius)
    }), {
      maxX: -Infinity,
      maxY: -Infinity,
      minX: Infinity,
      minY: Infinity
    })
  }
}
